import './css/App.css';
import React, {useEffect, useRef, useState} from "react";
import packageJson from '../package.json';
import {loadingAtom} from "./atoms";
import {useRecoilState} from 'recoil';
import Loading from "./core/Loading";
import {useNavigate} from "react-router-dom";
import {clearAuthSession, getCurrentUserName} from "./utils/auth";
import {
    findPendingReceivablesByCustomerCompanyId,
    getCompanyOfCurrentUser,
    isUserAdmin,
    exportCustomersExcel,
    exportProductsExcel,
} from "./utils/requestUtils";
import homeIcon from './img/icons8-home-24.png';
import {normalizeDate, parseLocalDate, UserPermission, CompanyPermission} from "./utils/utils";
import {makeRequest} from "./utils/httpRequest";
import Config from "./core/config";
import PendingReceivables from "./components/receivable/PendingReceivables";
import {usePermission} from "./core/PermissionContext";
import CustomerKeyLink from './CustomerKeyLInk';
import MainPage from './MainPage';
import {Link} from 'react-router-dom';
import {MenuIcon, UserIcon} from './components/common/actionIcons';
import {focusCurrentTarget, focusInteractiveClickTarget} from './utils/focusUtils';

const apiUrl = Config.apiUrl;
const COMPACT_MENU_MAX_WIDTH = 768;
const frontendVersion = packageJson.version;

function Menu({children}) {

    const [currentLoading, setCurrentLoading] = useRecoilState(loadingAtom);

    const [activeMenu, setActiveMenu] = useState(null);
    const navigate = useNavigate();
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const [userAdmin, setUserAdmin] = useState(false);
    const [pendingReceivables, setPendingReceivables] = useState(undefined);
    const [overdueReceivables, setOverdueReceivables] = useState(undefined);
    const [showCustomerKeyLink, setShowCustomerKeyLink] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [backendVersion, setBackendVersion] = useState(null);

    useEffect(() => {
        isUserAdmin().then(userAdmin => setUserAdmin(userAdmin));
    }, []);

    const MENUS_WITH_SUBMENU = [
        'Cliente', 'Produto', 'Aluguel', 'Financeiro', 'Entrada', 'Relatórios',
        'Usuários', 'Empresas', 'Prescrição', 'Links', 'Serviço', 'Mercado Pago',
        'Orçamentos', 'Vendas', 'Compras', 'Configurações'
    ];

    const [isMenuVisible, setMenuVisible] = useState(false);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isUserMenuOpen, setUserMenuOpen] = useState(false);

    const closeMenus = () => {
        setMenuVisible(false);
        setMobileMenuOpen(false);
        setUserMenuOpen(false);
    };

    const handleSubmenuTriggerClick = (event, menu) => {
        event.stopPropagation();
        focusCurrentTarget(event);

        if (activeMenu === menu && isMenuVisible) {
            setMenuVisible(false);
            setActiveMenu(null);
            return;
        }

        setActiveMenu(menu);
        setMenuVisible(MENUS_WITH_SUBMENU.includes(menu));
        setUserMenuOpen(false);
    };

    const handleUserMenuToggle = (event) => {
        event.stopPropagation();
        focusCurrentTarget(event);
        setUserMenuOpen((open) => !open);
        setMenuVisible(false);
        setActiveMenu(null);
    };
    const menuRef = useRef(null);
    const appShellRef = useRef(null);

    useEffect(() => {
        const shell = appShellRef.current;
        if (!shell) {
            return undefined;
        }

        const handleClickCapture = (event) => focusInteractiveClickTarget(event);
        shell.addEventListener('click', handleClickCapture, true);
        return () => shell.removeEventListener('click', handleClickCapture, true);
    }, []);

    const [companyName, setCompanyName] = useState(undefined);
    const [userName, setUserName] = useState(undefined);
    useEffect(() => {

        async function fetchData() {
            try {
                setUserName(getCurrentUserName());

                const result = await getCompanyOfCurrentUser();
                setCompanyName(result.name);
            } catch (error) {
                console.error('Erro ao buscar os dados:', error);
            }
        }

        fetchData();

        const handleOutsideClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                closeMenus();
            }
        };

        const mediaQuery = window.matchMedia(`(max-width: ${COMPACT_MENU_MAX_WIDTH}px)`);
        const handleViewportChange = () => {
            if (!mediaQuery.matches) {
                setMobileMenuOpen(false);
            }
        };
        mediaQuery.addEventListener('change', handleViewportChange);

        // Adiciona um ouvinte de evento global para cliques fora do menu
        document.addEventListener('click', handleOutsideClick);

        // Remove o ouvinte de evento quando o componente é desmontado
        return () => {
            document.removeEventListener('click', handleOutsideClick);
            mediaQuery.removeEventListener('change', handleViewportChange);
            document.body.classList.remove('app-menu-open');
        };
    }, []); // Executa apenas uma vez durante a montagem do componente

    useEffect(() => {
        const isCompactViewport = window.matchMedia(`(max-width: ${COMPACT_MENU_MAX_WIDTH}px)`).matches;
        const shouldLockScroll = isCompactViewport && (isMobileMenuOpen || isUserMenuOpen);

        if (shouldLockScroll) {
            document.body.classList.add('app-menu-open');
        } else {
            document.body.classList.remove('app-menu-open');
        }

        return () => {
            document.body.classList.remove('app-menu-open');
        };
    }, [isMobileMenuOpen, isUserMenuOpen]);

    useEffect(() => {
        findPendingReceivablesByCustomerCompanyId().then(pendingReceivables => setPendingReceivables(pendingReceivables));
    }, [userName]);

    useEffect(() => {
        const today = normalizeDate(new Date());

        const overdue = pendingReceivables?.filter(receivable => {
            const dueDate = receivable.dueDate ? normalizeDate(parseLocalDate(receivable.dueDate)) : null;
            return receivable.status === 0 && dueDate && dueDate < today;
        });

        setOverdueReceivables(overdue);
    }, [pendingReceivables]);


    const openAboutModal = () => {
        setShowAboutModal(true);
        closeMenus();
        makeRequest(`${apiUrl}/api/version`, 'GET')
            .then(data => setBackendVersion(data.version))
            .catch(() => setBackendVersion('indisponível'));
    };

    function handleExitClickButton() {
        clearAuthSession();
        localStorage.removeItem('products');
        localStorage.removeItem('customers');
        localStorage.removeItem('tables');
        localStorage.removeItem('sale');
        navigate('/login');
    }

    if (loadingPermissions) {
        return <Loading/>;
    }

    const url = window.location.pathname;
    const admin = url.startsWith('/admin') && userAdmin;
    const canChangePassword =
        hasUserPermission(UserPermission.CHANGE_OWN_PASSWORD) &&
        hasCompanyPermission(CompanyPermission.CHANGE_OWN_PASSWORD);

    return (
        <div className="app-shell" ref={appShellRef}>
            {currentLoading && (<Loading/>)}
            <div
                ref={menuRef}
                className={`app-menu-bar${isMobileMenuOpen ? ' app-menu-bar--open' : ''}`}
            >
                <button
                    type="button"
                    className="app-menu-kebab"
                    aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                    aria-expanded={isMobileMenuOpen}
                    onClick={(e) => {
                        e.stopPropagation();
                        focusCurrentTarget(e);
                        setMobileMenuOpen((open) => !open);
                        setUserMenuOpen(false);
                    }}
                >
                    <MenuIcon/>
                </button>
                <div className="app-menu-items">
                <div>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            closeMenus();
                            navigate(admin ? '/admin' : '/app');
                        }}
                        className={'menu-button menu-button--home ' + (activeMenu === 'Inicio' ? 'active' : '')}
                        data-test="menu-home"
                    >
                        <img
                            src={homeIcon}
                            alt=""
                            aria-hidden="true"
                            className="menu-button__home-icon"
                            style={{width: '18px', height: '18px'}}
                        />
                        <span className="menu-button__home-label">Página Inicial</span>
                    </div>
                </div>
                {/* Only show other menus if not locked to home */}
                {(overdueReceivables?.length === 0 && !admin) && (
                    <>
                        {/* Cliente Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_CUSTOMER) || hasUserPermission(UserPermission.VIEW_CUSTOMER_STATISTICS) || hasUserPermission(UserPermission.VIEW_KIDS_BIRTHDAY) || hasUserPermission(UserPermission.REPORT_CUSTOMER_AGE_RANGE)) &&
                                <div
                                    data-test="menu-customers"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Cliente')}
                                    className={'menu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                >
                                    Cliente
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Cliente' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_CUSTOMER) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/customerlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-customers-list"
                                            >
                                                Consultar Cliente
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_CUSTOMER) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/customer');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-customers-manage"
                                            >
                                                Cadastrar Cliente
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.VIEW_KIDS_BIRTHDAY) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/kidsbirthday');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-clientes-aniversariantes"
                                            >
                                                Consultar Aniversariantes
                                            </div>
                                        </li>
                                    }
                                    {/* TODO: remover código obsoleto da listagem de e-mails (CustomerEmail, rota /app/customeremail, VIEW_CUSTOMER_EMAIL) */}
                                    {hasUserPermission(UserPermission.VIEW_CUSTOMER_STATISTICS) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/customerstatistics');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-clientes-estatistica"
                                            >
                                                Estatística de Clientes
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.REPORT_CUSTOMER_AGE_RANGE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/customeragerange');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-clientes-faixa-etaria"
                                            >
                                                Relatório por Faixa Etária
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_CUSTOMER_KEY_LINK) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/customerkeylink');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-clientes-link"
                                            >
                                                Link de Cadastro
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.EXPORT_CUSTOMER_EXCEL) && hasCompanyPermission(CompanyPermission.EXPORT_CUSTOMER_EXCEL) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    exportCustomersExcel();
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Cliente' ? 'active' : '')}
                                                data-test="submenu-clientes-exportar-excel"
                                            >
                                                Exportar para Excel
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Produto Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_PRODUCT) || hasUserPermission(UserPermission.VIEW_PRODUCT_STATISTICS)) &&
                                <div
                                    data-test="menu-products"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Produto')}
                                    className={'menu-button ' + (activeMenu === 'Produto' ? 'active' : '')}
                                >
                                    Produto
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Produto' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_PRODUCT) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/productlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Produto' ? 'active' : '')}
                                                data-test="submenu-products-list"
                                            >
                                                Consultar Produto
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_PRODUCT) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/product');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Produto' ? 'active' : '')}
                                                data-test="submenu-products-manage"
                                            >
                                                Cadastrar Produto
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.VIEW_PRODUCT_STATISTICS) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/productstatistics');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Produto' ? 'active' : '')}
                                                data-test="submenu-produtos-estatistica"
                                            >
                                                Estatística de Produtos
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.EXPORT_PRODUCT_EXCEL) && hasCompanyPermission(CompanyPermission.EXPORT_PRODUCT_EXCEL) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    exportProductsExcel();
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Produto' ? 'active' : '')}
                                                data-test="submenu-produtos-exportar-excel"
                                            >
                                                Exportar para Excel
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Aluguel Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_RENTAL) || hasUserPermission(UserPermission.CREATE_RENTAL) || hasUserPermission(UserPermission.LIST_BUDGET) || hasUserPermission(UserPermission.CREATE_BUDGET) || hasUserPermission(UserPermission.LIST_PRODUCT_RENTED_DELIVERY_CONTROL)) &&
                                <div
                                    data-test="menu-rentals"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Aluguel')}
                                    className={'menu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                >
                                    Aluguel
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Aluguel' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_RENTAL) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/rentallist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                data-test="submenu-rentals-list"
                                            >
                                                Consultar Aluguel
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_RENTAL) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/rental');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                data-test="submenu-rentals-manage"
                                            >
                                                Cadastrar Aluguel
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_BUDGET) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/rentallist?isBudget=true');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                data-test="submenu-budgets-list"
                                            >
                                                Consultar Orçamentos
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_BUDGET) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/rental?isBudget=true');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                data-test="submenu-budgets-manage"
                                            >
                                                Criar Orçamento
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_PRODUCT_RENTED_DELIVERY_CONTROL) &&
                                        <>
                                            <li>
                                                <div
                                                    onClick={() => {
                                                        navigate('/app/productrentedlisttodeliver');
                                                        closeMenus();
                                                    }}
                                                    className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                    data-test="submenu-rentals-delivery"
                                                >
                                                    Controle de Entregas
                                                </div>
                                            </li>
                                            <li>
                                                <div
                                                    onClick={() => {
                                                        navigate('/app/productrentedlisttoreturn');
                                                        closeMenus();
                                                    }}
                                                    className={'submenu-button ' + (activeMenu === 'Aluguel' ? 'active' : '')}
                                                    data-test="submenu-rentals-return"
                                                >
                                                    Controle de Devoluções
                                                </div>
                                            </li>
                                        </>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Serviço Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_SERVICE) || hasUserPermission(UserPermission.LIST_SECTOR) || hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE)) &&
                                <div
                                    data-test="menu-services"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Serviço')}
                                    className={'menu-button ' + (activeMenu === 'Serviço' ? 'active' : '')}
                                >
                                    Serviço
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Serviço' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_SERVICE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/salelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Serviço' ? 'active' : '')}
                                                data-test="submenu-services-list"
                                            >
                                                Serviços
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_SECTOR) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/sectorlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Serviço' ? 'active' : '')}
                                                data-test="submenu-sectors-list"
                                            >
                                                Setores
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/automotiveservicelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Serviço' ? 'active' : '')}
                                                data-test="submenu-automotive-services-list"
                                            >
                                                Serviços Automotivos
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE_PRODUCTS) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/productautomotiveservicelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Serviço' ? 'active' : '')}
                                                data-test="submenu-automotive-service-products-list"
                                            >
                                                Produtos de Serviço Automotivo
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Vendas Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_AFTER_SALE) ||
                              (hasUserPermission(UserPermission.LIST_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.LIST_PRODUCT_SALE)) ||
                              (hasUserPermission(UserPermission.CREATE_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.CREATE_PRODUCT_SALE))) &&
                                <div
                                    data-test="menu-sales"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Vendas')}
                                    className={'menu-button ' + (activeMenu === 'Vendas' ? 'active' : '')}
                                >
                                    Vendas
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Vendas' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.LIST_PRODUCT_SALE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/productsalelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Vendas' ? 'active' : '')}
                                                data-test="submenu-product-sales-list"
                                            >
                                                Consultar Vendas
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.CREATE_PRODUCT_SALE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/productsale');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Vendas' ? 'active' : '')}
                                                data-test="submenu-product-sales-new"
                                            >
                                                Cadastrar Venda
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_AFTER_SALE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/aftersalelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Vendas' ? 'active' : '')}
                                                data-test="submenu-after-sales-list"
                                            >
                                                Pós-Vendas
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Compras Menu */}
                        <div>
                            {(hasUserPermission(UserPermission.LIST_SHOPPING_LIST) || hasUserPermission(UserPermission.CREATE_SHOPPING_LIST)) &&
                                (hasCompanyPermission(CompanyPermission.LIST_SHOPPING_LIST) || hasCompanyPermission(CompanyPermission.CREATE_SHOPPING_LIST)) &&
                                <div
                                    data-test="menu-shopping-lists"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Compras')}
                                    className={'menu-button ' + (activeMenu === 'Compras' ? 'active' : '')}
                                >
                                    Compras
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Compras' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_SHOPPING_LIST) &&
                                        hasCompanyPermission(CompanyPermission.LIST_SHOPPING_LIST) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/shoppinglistlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Compras' ? 'active' : '')}
                                                data-test="submenu-shopping-lists"
                                            >
                                                Listas de Compras
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.CREATE_SHOPPING_LIST) &&
                                        hasCompanyPermission(CompanyPermission.CREATE_SHOPPING_LIST) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/shoppinglist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Compras' ? 'active' : '')}
                                                data-test="submenu-shopping-list-new"
                                            >
                                                Nova Lista
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Financeiro Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_RECEIVABLE) || hasUserPermission(UserPermission.LIST_PAYMENT) || hasUserPermission(UserPermission.LIST_PAYMENT_METHOD) || hasUserPermission(UserPermission.LIST_PAYMENT_TERM)) &&
                                <div
                                    data-test="menu-receivables"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Financeiro')}
                                    className={'menu-button ' + (activeMenu === 'Financeiro' ? 'active' : '')}
                                >
                                    Financeiro
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Financeiro' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_RECEIVABLE) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/receivablelist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Financeiro' ? 'active' : '')}
                                                data-test="submenu-receivables-list"
                                            >
                                                Contas a Receber
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_PAYMENT) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/paymentlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Financeiro' ? 'active' : '')}
                                                data-test="submenu-financeiro-pagar"
                                            >
                                                Contas a Pagar
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_PAYMENT_TERM) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/paymenttermlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Financeiro' ? 'active' : '')}
                                                data-test="submenu-financeiro-condicoes"
                                            >
                                                Condições de Pagamento
                                            </div>
                                        </li>
                                    }
                                    {hasUserPermission(UserPermission.LIST_PAYMENT_METHOD) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/paymentmethodlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Financeiro' ? 'active' : '')}
                                                data-test="submenu-financeiro-formas"
                                            >
                                                Formas de Pagamento
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Entrada Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_SUPPLIER)) &&
                                <div
                                    data-test="menu-entrada"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Entrada')}
                                    className={'menu-button ' + (activeMenu === 'Entrada' ? 'active' : '')}
                                >
                                    Entrada
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Entrada' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_SUPPLIER) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/supplierlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Entrada' ? 'active' : '')}
                                                data-test="submenu-entrada-fornecedores"
                                            >
                                                Fornecedores
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Prescrição Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.LIST_PRESCRIPTION)) &&
                                <div
                                    data-test="menu-prescricao"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Prescrição')}
                                    className={'menu-button ' + (activeMenu === 'Prescrição' ? 'active' : '')}
                                >
                                    Prescrição
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Prescrição' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.LIST_PRESCRIPTION) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/prescriptionlist');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Prescrição' ? 'active' : '')}
                                                data-test="submenu-prescricao-prescricoes"
                                            >
                                                Prescrições
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>

                        {/* Relatórios Menu */}
                        <div >
                            {(hasUserPermission(UserPermission.REPORT_SALES_BY_SECTOR) ||
                                    hasUserPermission(UserPermission.REPORT_SALES_BY_USER) ||
                                    hasUserPermission(UserPermission.REPORT_RECEIVABLES_BY_PAYMENT_METHOD) ||
                                    hasUserPermission(UserPermission.REPORT_RECEIVABLES_DETAILED) || hasUserPermission(UserPermission.REPORT_PAID_RECEIVABLES_BY_USER) ||
                                    hasUserPermission(UserPermission.REPORT_AUTOMOTIVE_SERVICE_BY_ITEM)) &&
                                <div
                                    data-test="menu-relatorios"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Relatórios')}
                                    className={'menu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                >
                                    Relatórios
                                </div>
                            }

                            {isMenuVisible && activeMenu === 'Relatórios' && (
                                <ul className="submenu">
                                    {hasUserPermission(UserPermission.REPORT_SALES_BY_SECTOR) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/salesbysector');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-vendas-setor"
                                            >
                                                Vendas por setor
                                            </div>
                                        </li>
                                    }

                                    {hasUserPermission(UserPermission.REPORT_SALES_BY_USER) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/salesbyuser');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-vendas-vendedor"
                                            >
                                                Vendas por vendedor
                                            </div>
                                        </li>
                                    }

                                    {hasUserPermission(UserPermission.REPORT_RECEIVABLES_BY_PAYMENT_METHOD) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/receivablesbypaymentmethod');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-fechamento-caixa-resumido"
                                            >
                                                Fechamento de Caixa Resumido
                                            </div>
                                        </li>
                                    }

                                    {hasUserPermission(UserPermission.REPORT_RECEIVABLES_DETAILED) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/receivables');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-fechamento-caixa-detalhado"
                                            >
                                                Fechamento de Caixa Detalhado
                                            </div>
                                        </li>
                                    }

                                    {hasUserPermission(UserPermission.REPORT_PAID_RECEIVABLES_BY_USER) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/paidreceivablebybyuser');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-recebimentos-vendedor"
                                            >
                                                Recebimentos por Vendedor
                                            </div>
                                        </li>
                                    }

                                    {hasUserPermission(UserPermission.REPORT_AUTOMOTIVE_SERVICE_BY_ITEM) &&
                                        <li>
                                            <div
                                                onClick={() => {
                                                    navigate('/app/reports/automotiveservicebyitem');
                                                    closeMenus();
                                                }}
                                                className={'submenu-button ' + (activeMenu === 'Relatórios' ? 'active' : '')}
                                                data-test="submenu-relatorios-servicos-automotivos-item"
                                            >
                                                Serviços Automotivos por Item
                                            </div>
                                        </li>
                                    }
                                </ul>
                            )}
                        </div>
                    </>
                )}
                        {/* Empresas Menu */}
                        {(userAdmin || hasUserPermission('LIST_COMPANY') || hasUserPermission('CREATE_COMPANY') || hasUserPermission('EDIT_COMPANY')) && (
                            <div >
                                <div
                                    data-test="menu-empresas"
                                    onClick={(e) => handleSubmenuTriggerClick(e, 'Empresas')}
                                    className={'menu-button ' + (activeMenu === 'Empresas' ? 'active' : '')}
                                >
                                    Empresas
                                </div>
                                {isMenuVisible && activeMenu === 'Empresas' && (
                                    <ul className="submenu">
                                        {(userAdmin || hasUserPermission('LIST_COMPANY')) && (
                                            <li>
                                                <div
                                                    data-test="submenu-empresas"
                                                    onClick={() => {
                                                        navigate(`/${userAdmin ? 'admin' : 'app'}/companylist`);
                                                        closeMenus();
                                                    }}
                                                    className={'submenu-button ' + (activeMenu === 'Empresas' ? 'active' : '')}
                                                >
                                                    Empresas
                                                </div>
                                            </li>
                                        )}
                                        {userAdmin && (
                                            <li>
                                                <div
                                                    data-test="submenu-empresas-grupos"
                                                    onClick={() => {
                                                        navigate('/admin/companygrouplist');
                                                        closeMenus();
                                                    }}
                                                    className={'submenu-button ' + (activeMenu === 'Empresas' ? 'active' : '')}
                                                >
                                                    Grupos de Permissões
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                {overdueReceivables?.length === 0 && (userAdmin || hasUserPermission(UserPermission.LIST_USER)) &&
                    <div >
                        <div
                            data-test="menu-usuarios"
                            onClick={(e) => handleSubmenuTriggerClick(e, 'Usuários')}
                            className={'menu-button ' + (activeMenu === 'Usuários' ? 'active' : '')}
                        >
                            Usuários
                        </div>

                        {isMenuVisible && activeMenu === 'Usuários' && (
                            <ul className="submenu">
                                <li>
                                    <div
                                        data-test="submenu-usuarios-usuarios"
                                        onClick={() => {
                                            navigate(`/${userAdmin ? 'admin' : 'app'}/userlist`);
                                            closeMenus();
                                        }}
                                        className={'submenu-button ' + (activeMenu === 'Usuários' ? 'active' : '')}
                                    >
                                        Usuários
                                    </div>
                                </li>
                                {userAdmin && (
                                    <li>
                                        <div
                                            data-test="submenu-system-usage"
                                            onClick={() => {
                                                navigate('/admin/system-usage');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Usuários' ? 'active' : '')}
                                        >
                                            Utilização do Sistema
                                        </div>
                                    </li>
                                )}
                                {userAdmin && (
                                    <li>
                                        <div
                                            data-test="submenu-system-feedback"
                                            onClick={() => {
                                                navigate('/admin/system-feedback');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Usuários' ? 'active' : '')}
                                        >
                                            Avaliações do Sistema
                                        </div>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                }

                {/* Configurações Menu */}
                {hasCompanyPermission &&
                 ((hasUserPermission(UserPermission.MANAGE_COMPANY_CLOSED_DAYS) &&
                   hasCompanyPermission(CompanyPermission.MANAGE_COMPANY_CLOSED_DAYS)) ||
                  (hasUserPermission(UserPermission.MANAGE_FREIGHT_CONFIGURATION) &&
                   hasCompanyPermission(CompanyPermission.MANAGE_FREIGHT_CONFIGURATION)) ||
                  (hasUserPermission(UserPermission.MANAGE_PIX_CONFIGURATION) &&
                   hasCompanyPermission(CompanyPermission.MANAGE_PIX_CONFIGURATION)) ||
                  hasUserPermission(UserPermission.CONFIGURE_SALE_DEFAULT_SECTORS)) && (
                    <div >
                        <div
                            data-test="menu-configuracoes"
                            onClick={(e) => handleSubmenuTriggerClick(e, 'Configurações')}
                            className={'menu-button ' + (activeMenu === 'Configurações' ? 'active' : '')}
                        >
                            Configurações
                        </div>

                        {isMenuVisible && activeMenu === 'Configurações' && (
                            <ul className="submenu">
                                {hasUserPermission(UserPermission.MANAGE_COMPANY_CLOSED_DAYS) &&
                                 hasCompanyPermission(CompanyPermission.MANAGE_COMPANY_CLOSED_DAYS) && (
                                    <li>
                                        <div
                                            data-test="submenu-configuracoes-diasoff"
                                            onClick={() => {
                                                navigate('/app/settings/closed-days');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Configurações' ? 'active' : '')}
                                        >
                                            Dias off
                                        </div>
                                    </li>
                                )}
                                {hasUserPermission(UserPermission.MANAGE_FREIGHT_CONFIGURATION) &&
                                 hasCompanyPermission(CompanyPermission.MANAGE_FREIGHT_CONFIGURATION) && (
                                    <li>
                                        <div
                                            data-test="submenu-configuracoes-frete"
                                            onClick={() => {
                                                navigate('/app/settings/freight');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Configurações' ? 'active' : '')}
                                        >
                                            Frete
                                        </div>
                                    </li>
                                )}
                                {hasUserPermission(UserPermission.MANAGE_PIX_CONFIGURATION) &&
                                 hasCompanyPermission(CompanyPermission.MANAGE_PIX_CONFIGURATION) && (
                                    <li>
                                        <div
                                            data-test="submenu-configuracoes-pix"
                                            onClick={() => {
                                                navigate('/app/settings/pix-key');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Configurações' ? 'active' : '')}
                                        >
                                            Chave Pix
                                        </div>
                                    </li>
                                )}
                                {hasUserPermission(UserPermission.CONFIGURE_SALE_DEFAULT_SECTORS) && (
                                    <li>
                                        <div
                                            data-test="submenu-configuracoes-setores-padrao"
                                            onClick={() => {
                                                navigate('/app/settings/sale-default-sectors');
                                                closeMenus();
                                            }}
                                            className={'submenu-button ' + (activeMenu === 'Configurações' ? 'active' : '')}
                                        >
                                            Setores padrão
                                        </div>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                )}

                {/* WhatsApp */}
                {hasUserPermission(UserPermission.WHATSAPP_VIEW) &&
                <div
                    data-test="menu-whatsapp"
                    onClick={() => { navigate('/app/whatsapp'); closeMenus(); }}
                    className={'menu-button ' + (activeMenu === 'WhatsApp' ? 'active' : '')}
                >
                    WhatsApp
                </div>
                }

                {/* Mercado Pago Menu */}
                <div >
                    {(admin) &&
                        <div
                            data-test="menu-receivables"
                            onClick={(e) => handleSubmenuTriggerClick(e, 'Mercado Pago')}
                            className={'menu-button ' + (activeMenu === 'Mercado Pago' ? 'active' : '')}
                        >
                            Mercado Pago
                        </div>
                    }

                    {isMenuVisible && activeMenu === 'Mercado Pago' && (
                        <ul className="submenu">
                            {userAdmin &&
                                <li>
                                    <div
                                        onClick={() => {
                                            navigate('/admin/paymentmethodmercadopagolist');
                                            closeMenus();
                                        }}
                                        className={'submenu-button ' + (activeMenu === 'Mercado Pago' ? 'active' : '')}
                                        data-test="submenu-financeiro-formas-mercado-pago"
                                    >
                                        Formas de Pagamento Mercado Pago
                                    </div>
                                </li>
                            }
                        </ul>
                    )}
                </div>
                </div>
                <div className="app-menu-user">
                    <button
                        type="button"
                        className="app-menu-user-trigger"
                        aria-label={isUserMenuOpen ? 'Fechar menu do usuário' : 'Abrir menu do usuário'}
                        aria-expanded={isUserMenuOpen}
                        data-test="menu-user"
                        onClick={handleUserMenuToggle}
                    >
                        <UserIcon/>
                    </button>
                    {isUserMenuOpen && (
                        <ul className="app-menu-user-dropdown">
                            <li className="app-menu-user-details">
                                <div className="app-menu-user-name">{userName || '—'}</div>
                                <div className="app-menu-user-company">{companyName || '—'}</div>
                            </li>
                            {canChangePassword && (
                                <li className="app-menu-user-actions">
                                    <button
                                        type="button"
                                        data-test="menu-change-password"
                                        onClick={() => {
                                            navigate(url.startsWith('/admin') ? '/admin/change-password' : '/app/change-password');
                                            closeMenus();
                                        }}
                                        className="submenu-button app-menu-user-action"
                                    >
                                        Alterar senha
                                    </button>
                                </li>
                            )}
                            <li className="app-menu-user-actions">
                                <button
                                    type="button"
                                    data-test="menu-sobre"
                                    onClick={openAboutModal}
                                    className="submenu-button app-menu-user-action"
                                >
                                    Sobre
                                </button>
                            </li>
                            <li className="app-menu-user-actions">
                                <button
                                    type="button"
                                    data-test="menu-sair"
                                    onClick={() => {
                                        closeMenus();
                                        handleExitClickButton();
                                    }}
                                    className="submenu-button app-menu-user-action"
                                >
                                    Sair
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            </div>
            <div className="app-shell__content">
                {children}
            </div>
            {showAboutModal && (
                <div className="modal-overlay" onClick={() => setShowAboutModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '320px', textAlign: 'center'}}>
                        <h3 style={{marginTop: 0}}>Sobre</h3>
                        <p>Frontend: <strong>v{frontendVersion}</strong></p>
                        <p>Backend: <strong>{backendVersion !== null ? `v${backendVersion}` : 'carregando...'}</strong></p>
                        <button onClick={() => setShowAboutModal(false)}>Fechar</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Menu;
